/**
 * Persistent export-asset fetch cache (P1b).
 *
 * A SEPARATE IndexedDB database from AssetStore, deliberately. AssetStore holds
 * project data the user owns and expects to survive; this holds a disposable
 * copy of somebody else's bytes. Different lifecycle, different GC, different
 * privacy contract — sharing a database would eventually mean a cache sweep
 * deleting project assets, or a project GC keeping cache entries alive.
 *
 * Three properties this file exists to guarantee:
 *
 *  1. PRINCIPAL ISOLATION. The cache key is prefixed with the signed-in
 *     principal, so two accounts on one browser cannot observe each other's
 *     asset reuse. Isolation is structural (it is in the key), not merely a
 *     filter that a future refactor could forget. The partition is dropped on
 *     sign-out and on an account switch.
 *  2. NO RAW SIGNED URLS AT REST. Keys and stored `url` fields are normalized:
 *     credentials and every known signing/expiry parameter are stripped before
 *     anything is written. A cache is not a place to persist a bearer token.
 *  3. IT CAN ALWAYS BE TURNED OFF. Quota exhaustion, private browsing, a
 *     missing indexedDB, a hostile storage policy — every failure degrades to
 *     cache-off and the export proceeds. A cache that can fail an export is
 *     worse than no cache.
 *
 * Freshness follows the response: Cache-Control max-age when present, else a
 * default positive TTL; ETag / Last-Modified are retained so an expired entry
 * can be revalidated rather than re-downloaded. Negative entries carry a
 * per-reason TTL from the P1a taxonomy — and `aborted` is NEVER cached, because
 * the operator cancelling is not a fact about the URL.
 */

import { createIdbHandle, withStore, promisifyRequest } from './idb.js';
import { reportError } from './report-error.js';

const DB_NAME = 'pb-asset-fetch';
const DB_VERSION = 1;
const META = 'meta';
const BLOBS = 'blobs';

/** Skip caching a single response larger than this. */
export const MAX_ENTRY_BYTES = 8 * 1024 * 1024;
/** Sweep oldest-first once the cache exceeds this. */
export const MAX_TOTAL_BYTES = 200 * 1024 * 1024;
/** Positive TTL when the response says nothing. */
const DEFAULT_POSITIVE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Negative TTLs, per P1a failure reason. Transient network conditions expire
 * quickly; a definite answer from the origin is trusted for longer. `aborted`
 * is absent on purpose — see storeAssetFetchMiss.
 */
const NEGATIVE_TTL_MS = Object.freeze({
  'http-4xx': 30 * 60 * 1000,
  'invalid-mime': 30 * 60 * 1000,
  'http-5xx': 5 * 60 * 1000,
  timeout: 5 * 60 * 1000,
  network: 5 * 60 * 1000,
  cors: 5 * 60 * 1000,
  proxy: 2 * 60 * 1000
});

/** Query params that carry a signature, credential or expiry. Never persisted. */
const SIGNING_PARAMS = new Set([
  'sig',
  'signature',
  'expires',
  'token',
  'access_token',
  'awsaccesskeyid',
  'policy',
  'x-amz-signature',
  'x-amz-credential',
  'x-amz-security-token',
  'x-amz-date',
  'x-amz-expires',
  'x-amz-algorithm',
  'x-amz-signedheaders',
  'x-goog-signature',
  'x-goog-credential'
]);

const cacheIdb = createIdbHandle({
  name: DB_NAME,
  version: DB_VERSION,
  upgrade: (db) => {
    if (!db.objectStoreNames.contains(META)) {
      const store = db.createObjectStore(META, { keyPath: 'key' });
      store.createIndex('principal', 'principal');
    }
    if (!db.objectStoreNames.contains(BLOBS)) {
      db.createObjectStore(BLOBS, { keyPath: 'key' });
    }
  }
});

let _principal = null;
let _disabled = false;
const _inFlight = new Map();

/** True while the cache is usable. False after any storage failure. */
export function isAssetFetchCacheEnabled() {
  return !_disabled && typeof globalThis !== 'undefined' && !!globalThis.indexedDB;
}

/**
 * Turn the cache off for the rest of the session. Called on any storage error:
 * quota exhaustion, private browsing, a blocked open. Reported once, silently —
 * a degraded cache is not a user-facing failure, but it must not be invisible
 * to the console either.
 */
function disable(err, where) {
  if (_disabled) return;
  _disabled = true;
  reportError('AssetFetchCache', err, {
    user: 'silent',
    level: 'warn',
    detail: `asset fetch cache disabled after a failure in ${where}; exports continue uncached`
  });
}

/**
 * Set the active principal. Mirrors initExportEventQueue's contract in
 * core/export-event-queue.js so there is one shape for "who is signed in" in
 * persistent stores. A change of principal — including sign-out (null) — drops
 * the OUTGOING principal's rows.
 */
export async function setAssetFetchCachePrincipal(principalSub) {
  const next = principalSub ? String(principalSub) : null;
  if (next === _principal) return;
  const previous = _principal;
  _principal = next;
  _inFlight.clear();
  if (previous) await clearPrincipal(previous);
}

/** Drop every row belonging to one principal. Used on sign-out / switch. */
export async function clearPrincipal(principal) {
  if (!isAssetFetchCacheEnabled() || !principal) return;
  try {
    const rows = await withStore(cacheIdb, META, 'readonly', (store) =>
      promisifyRequest(store.index('principal').getAll(principal))
    );
    const keys = rows.map((r) => r.key);
    if (!keys.length) return;
    await withStore(cacheIdb, META, 'readwrite', (store) => {
      for (const k of keys) store.delete(k);
    });
    await withStore(cacheIdb, BLOBS, 'readwrite', (store) => {
      for (const k of keys) store.delete(k);
    });
  } catch (err) {
    disable(err, 'clearPrincipal');
  }
}

/**
 * Strip credentials and signing parameters, and order what remains, so the same
 * logical asset behind two differently-signed URLs is one cache entry and no
 * secret is written to disk.
 */
export function normalizeCacheUrl(rawUrl) {
  const raw = String(rawUrl || '');
  let parsed;
  try {
    parsed = new URL(raw.startsWith('//') ? `https:${raw}` : raw);
  } catch {
    return raw.split('?')[0];
  }
  parsed.username = '';
  parsed.password = '';
  parsed.hash = '';
  const kept = [];
  for (const [k, v] of parsed.searchParams.entries()) {
    if (SIGNING_PARAMS.has(k.toLowerCase())) continue;
    kept.push([k, v]);
  }
  kept.sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0));
  const query = kept.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&');
  return `${parsed.origin}${parsed.pathname}${query ? `?${query}` : ''}`;
}

/**
 * Separator between the principal and the URL in a cache key. NUL is used
 * because it cannot appear in a URL, so no URL can ever forge a key belonging
 * to another principal. Written as an escape, not a literal: a raw control
 * character in source is invisible in diffs and in every editor.
 */
const KEY_SEP = '\u0000';

/**
 * Cache key. The principal is IN the key, so isolation survives any future
 * refactor that forgets to filter on it. See KEY_SEP.
 */
function cacheKey(normalizedUrl) {
  return `${_principal || 'anon'}${KEY_SEP}${normalizedUrl}`;
}

function maxAgeFrom(cacheControl) {
  const m = /(?:^|,)\s*max-age\s*=\s*(\d+)/i.exec(String(cacheControl || ''));
  if (!m) return null;
  const seconds = Number(m[1]);
  return Number.isFinite(seconds) && seconds >= 0 ? seconds * 1000 : null;
}

function noStore(cacheControl) {
  return /(?:^|,)\s*no-store\s*(?:,|$)/i.test(String(cacheControl || ''));
}

function requiresRevalidation(cacheControl) {
  return /(?:^|,)\s*(?:no-cache|max-age\s*=\s*0)\s*(?:,|$)/i.test(String(cacheControl || ''));
}

/**
 * Look up a URL.
 * @returns {Promise<{status:'hit', blob:Blob, meta:object}
 *   | {status:'stale', meta:object}
 *   | {status:'miss', reason:string}
 *   | {status:'none'}>}
 */
export async function lookupAssetFetch(url, now = Date.now()) {
  if (!isAssetFetchCacheEnabled()) return { status: 'none' };
  const key = cacheKey(normalizeCacheUrl(url));
  try {
    const meta = await withStore(cacheIdb, META, 'readonly', (store) =>
      promisifyRequest(store.get(key))
    );
    if (!meta) return { status: 'none' };
    const expired = typeof meta.expiresAt === 'number' && meta.expiresAt <= now;
    if (meta.kind === 'miss') {
      if (expired) return { status: 'none' };
      return { status: 'miss', reason: meta.reason };
    }
    if (expired) {
      // Revalidatable only if the origin gave us a validator.
      if (meta.etag || meta.lastModified) return { status: 'stale', meta };
      return { status: 'none' };
    }
    const row = await withStore(cacheIdb, BLOBS, 'readonly', (store) =>
      promisifyRequest(store.get(key))
    );
    if (!row || !row.blob) return { status: 'none' };
    return { status: 'hit', blob: row.blob, meta };
  } catch (err) {
    disable(err, 'lookupAssetFetch');
    return { status: 'none' };
  }
}

/** Persist a successful download. Oversized responses are used but not stored. */
export async function storeAssetFetchHit(url, blob, headers = {}, now = Date.now()) {
  if (!isAssetFetchCacheEnabled() || !blob) return;
  const size = blob.size ?? 0;
  if (size <= 0 || size > MAX_ENTRY_BYTES) return;
  if (noStore(headers.cacheControl)) return;
  const normalized = normalizeCacheUrl(url);
  const key = cacheKey(normalized);
  const ttl = requiresRevalidation(headers.cacheControl)
    ? 0
    : (maxAgeFrom(headers.cacheControl) ?? DEFAULT_POSITIVE_TTL_MS);
  const meta = {
    key,
    principal: _principal || 'anon',
    url: normalized,
    kind: 'hit',
    mime: blob.type || '',
    size,
    etag: headers.etag || null,
    lastModified: headers.lastModified || null,
    cacheControl: headers.cacheControl || '',
    storedAt: now,
    expiresAt: now + ttl
  };
  try {
    await withStore(cacheIdb, BLOBS, 'readwrite', (store) => store.put({ key, blob }));
    await withStore(cacheIdb, META, 'readwrite', (store) => store.put(meta));
    await sweepIfNeeded();
  } catch (err) {
    disable(err, 'storeAssetFetchHit');
  }
}

/**
 * Persist a failure so the next export does not repeat it.
 *
 * `aborted` is never stored: the operator pressing Cancel says nothing about
 * whether the URL is reachable, and caching it would make a cancelled export
 * poison the next one. `skipped-circuit-open` is likewise not a fact about the
 * URL — it is a fact about the host during one run.
 */
export async function storeAssetFetchMiss(url, reason, now = Date.now()) {
  if (!isAssetFetchCacheEnabled()) return;
  const ttl = NEGATIVE_TTL_MS[reason];
  if (!ttl) return; // unknown, 'aborted', or 'skipped-circuit-open'
  const normalized = normalizeCacheUrl(url);
  const key = cacheKey(normalized);
  try {
    await withStore(cacheIdb, META, 'readwrite', (store) =>
      store.put({
        key,
        principal: _principal || 'anon',
        url: normalized,
        kind: 'miss',
        reason,
        storedAt: now,
        expiresAt: now + ttl
      })
    );
    // A prior body for this key is now wrong.
    await withStore(cacheIdb, BLOBS, 'readwrite', (store) => store.delete(key));
  } catch (err) {
    disable(err, 'storeAssetFetchMiss');
  }
}

/** After a 304: extend the entry without re-downloading its body. */
export async function refreshAssetFetch(url, headers = {}, now = Date.now()) {
  if (!isAssetFetchCacheEnabled()) return null;
  const key = cacheKey(normalizeCacheUrl(url));
  try {
    const meta = await withStore(cacheIdb, META, 'readonly', (store) =>
      promisifyRequest(store.get(key))
    );
    if (!meta || meta.kind !== 'hit') return null;
    // A 304 updates only the headers it carries; an omitted Cache-Control does
    // not erase the cached response's no-cache/max-age=0 directive. Losing it
    // here would turn one successful revalidation into seven days of reuse.
    const cacheControl = headers.cacheControl || meta.cacheControl || '';
    if (noStore(cacheControl)) {
      const row = await withStore(cacheIdb, BLOBS, 'readonly', (store) =>
        promisifyRequest(store.get(key))
      );
      await withStore(cacheIdb, META, 'readwrite', (store) => store.delete(key));
      await withStore(cacheIdb, BLOBS, 'readwrite', (store) => store.delete(key));
      return row?.blob || null;
    }
    const ttl = requiresRevalidation(cacheControl)
      ? 0
      : (maxAgeFrom(cacheControl) ?? DEFAULT_POSITIVE_TTL_MS);
    const next = {
      ...meta,
      etag: headers.etag || meta.etag,
      lastModified: headers.lastModified || meta.lastModified,
      cacheControl,
      storedAt: now,
      expiresAt: now + ttl
    };
    await withStore(cacheIdb, META, 'readwrite', (store) => store.put(next));
    const row = await withStore(cacheIdb, BLOBS, 'readonly', (store) =>
      promisifyRequest(store.get(key))
    );
    return row?.blob || null;
  } catch (err) {
    disable(err, 'refreshAssetFetch');
    return null;
  }
}

/**
 * Evict oldest-first when over the byte cap. Reads the META store ONLY — the
 * bodies live in a separate store precisely so a sweep never has to load
 * hundreds of megabytes of blobs into memory to decide what to delete.
 */
async function sweepIfNeeded() {
  const rows = await withStore(cacheIdb, META, 'readonly', (store) =>
    promisifyRequest(store.getAll())
  );
  let total = 0;
  for (const r of rows) total += r.size || 0;
  if (total <= MAX_TOTAL_BYTES) return;
  const byAge = rows.filter((r) => r.kind === 'hit').sort((a, b) => a.storedAt - b.storedAt);
  const doomed = [];
  for (const r of byAge) {
    if (total <= MAX_TOTAL_BYTES) break;
    doomed.push(r.key);
    total -= r.size || 0;
  }
  if (!doomed.length) return;
  await withStore(cacheIdb, META, 'readwrite', (store) => {
    for (const k of doomed) store.delete(k);
  });
  await withStore(cacheIdb, BLOBS, 'readwrite', (store) => {
    for (const k of doomed) store.delete(k);
  });
}

/**
 * Collapse concurrent requests for the same URL into one. Two sections
 * referencing the same hero image used to mean two downloads inside one export;
 * with a pool they can now be genuinely simultaneous.
 */
export function dedupeAssetFetch(url, fn) {
  const key = cacheKey(normalizeCacheUrl(url));
  const existing = _inFlight.get(key);
  if (existing) return existing;
  const p = (async () => {
    try {
      // Web Locks are origin-wide, so another tab reaches `fn` only after the
      // first tab has stored its hit/miss. `fn` begins with a cache lookup;
      // therefore the waiter observes that entry and performs zero network
      // fetches. The module Map remains the fast same-realm path and the
      // fallback where Web Locks are unavailable.
      const locks = globalThis.navigator?.locks;
      if (locks && typeof locks.request === 'function') {
        return await locks.request(`pb-asset-fetch:${key}`, { mode: 'exclusive' }, fn);
      }
      return await fn();
    } finally {
      _inFlight.delete(key);
    }
  })();
  _inFlight.set(key, p);
  return p;
}

/** Test isolation. Mirrors _resetVendoredStoreCache in asset-resolver.js. */
export function _resetAssetFetchCacheForTests({ principal = null } = {}) {
  _disabled = false;
  _principal = principal;
  _inFlight.clear();
  cacheIdb.reset();
}

/** Test/diagnostic: current principal partition. */
export function _assetFetchCachePrincipal() {
  return _principal;
}
